import { get, getRaw, patch, postRaw, uploadRaw } from "./client";
import type {
  AccountChoices,
  CategorizationListResponse,
  ImportJobStatus,
  ImportResult,
  ReviewAnswer,
} from "../types/transaction";

export async function uploadBankFile(
  clientId: number,
  file: File,
  bankAccount?: string,
): Promise<ImportResult> {
  const formData = new FormData();
  formData.append("file", file);

  const params = new URLSearchParams({ client_id: String(clientId) });
  if (bankAccount) {
    params.set("bank_account", bankAccount);
  }

  return uploadRaw<ImportResult>(`/api/clients/${clientId}/bank/upload?${params}`, formData);
}

export async function listCategorizations(
  clientId: number,
  status?: string,
  limit = 50,
  offset = 0,
): Promise<CategorizationListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (status) {
    params.set("status", status);
  }

  return getRaw<CategorizationListResponse>(`/api/clients/${clientId}/categorizations?${params}`);
}

export async function confirmCategorization(
  clientId: number,
  categorizationId: number,
): Promise<void> {
  await patch<unknown>(`/api/clients/${clientId}/categorizations/${categorizationId}`, {
    action: "confirm",
  });
}

export async function correctCategorization(
  clientId: number,
  categorizationId: number,
  newAccount: string,
): Promise<void> {
  await patch<unknown>(`/api/clients/${clientId}/categorizations/${categorizationId}`, {
    action: "correct",
    new_account: newAccount,
  });
}

/**
 * The answer of an undo (D1 amendment 2.8). The server sends `last_log_id`,
 * `can_undo` and `can_redo` on every undo, describing the row after the step;
 * `new_account` only when the account changed, `adjusts_period` only when a
 * month was adjusted (openapi.json UpdateCategorizationResponse).
 */
export interface UndoAnswer {
  status: string;
  new_account?: string | null;
  last_log_id: number | null;
  can_undo: boolean | null;
  can_redo: boolean | null;
  adjusts_period?: string | null;
}

/**
 * Undo the last change to one categorization.
 *
 * Sends PATCH action `undo` with the item's `last_log_id` as
 * `expected_last_log_id`, so the server refuses (STALE_VIEW) an undo of a
 * change the screen never showed. Resolves with the server's answer; a
 * refusal rejects with an ApiClientError carrying the server's code and
 * sentence word for word (api/client.ts).
 */
export async function undoCategorization(
  clientId: number,
  categorizationId: number,
  expectedLastLogId: number | null,
): Promise<UndoAnswer> {
  return patch<UndoAnswer>(`/api/clients/${clientId}/categorizations/${categorizationId}`, {
    action: "undo",
    expected_last_log_id: expectedLastLogId,
  });
}

/** One shown pending item and the newest log line it was built from. */
export interface ConfirmAllTarget {
  id: number;
  expected_last_log_id: number | null;
}

/** One item's outcome; `message` is the server's sentence when not confirmed. */
export interface ConfirmAllItem {
  id: number;
  status: "confirmed" | "failed" | "blocked";
  failure_code: string | null;
  message: string | null;
  adjusts_period?: string | null;
}

export interface ConfirmAllResponse {
  confirmed: number;
  failed: number;
  items: ConfirmAllItem[];
}

/**
 * Confirm exactly the given targets; the caller decides which are shown.
 *
 * The server answers 200 with one item per target even on partial failure,
 * a blocked or failed item carrying its own sentence in `message`. A refusal
 * of the whole request rejects with an ApiClientError (api/client.ts).
 */
export async function confirmAllCategorizations(
  clientId: number,
  targets: ConfirmAllTarget[],
): Promise<ConfirmAllResponse> {
  return postRaw<ConfirmAllResponse>(`/api/clients/${clientId}/categorizations/confirm-all`, {
    targets,
  });
}

/** The pick list's account names and the server's label for each it names. */
export async function listAccounts(clientId: number): Promise<AccountChoices> {
  const data = await getRaw<Partial<AccountChoices>>(`/api/clients/${clientId}/accounts`);
  return { accounts: data.accounts ?? [], labels: data.labels ?? {} };
}

function reviewPath(clientId: number, categorizationId: number): string {
  return `/api/clients/${clientId}/categorizations/${categorizationId}`;
}

/**
 * Redo the change the last undo took back.
 *
 * Sends PATCH action `redo` with the item's `last_log_id` as
 * `expected_last_log_id`; the server refuses (STALE_VIEW, STALE_REDO,
 * NOTHING_TO_REDO) by its own sentence, carried by the ApiClientError.
 */
export async function redoCategorization(
  clientId: number,
  categorizationId: number,
  expectedLastLogId: number | null,
): Promise<ReviewAnswer> {
  return patch<ReviewAnswer>(reviewPath(clientId, categorizationId), {
    action: "redo",
    expected_last_log_id: expectedLastLogId,
  });
}

/**
 * Answer a held row "not a transfer" with one named counterpart row.
 *
 * `otherTransactionId` is a transaction id from the row's fence; the answer's
 * `rescored` lists what became of each row of the pair.
 */
export async function notATransfer(
  clientId: number,
  categorizationId: number,
  otherTransactionId: number,
): Promise<ReviewAnswer> {
  return patch<ReviewAnswer>(reviewPath(clientId, categorizationId), {
    action: "not_a_transfer",
    not_a_transfer_with: otherTransactionId,
  });
}

/** Answer a held card credit as a refund from a store or vendor. */
export async function refundCategorization(
  clientId: number,
  categorizationId: number,
): Promise<ReviewAnswer> {
  return patch<ReviewAnswer>(reviewPath(clientId, categorizationId), { action: "refund" });
}

/** One upload's job status, by the `job_id` the upload answered. */
export async function getImportJob(clientId: number, jobId: string): Promise<ImportJobStatus> {
  return get<ImportJobStatus>(`/api/clients/${clientId}/bank/imports/${jobId}`);
}

export interface AlternativeItem {
  account: string;
  reason: string;
}

export interface AlternativesResponse {
  alternatives: AlternativeItem[];
  all_accounts: string[];
}

export async function fetchAlternatives(
  clientId: number,
  categorizationId: number,
): Promise<AlternativesResponse> {
  return getRaw<AlternativesResponse>(
    `/api/clients/${clientId}/categorizations/${categorizationId}/alternatives`,
  );
}

export interface SplitLine {
  account_name: string;
  amount: string;
  memo?: string;
}

interface SplitResult {
  status: string;
  split_ids: number[];
  lines: number;
  reversed_original: boolean;
}

export async function splitTransaction(
  clientId: number,
  categorizationId: number,
  lines: SplitLine[],
): Promise<SplitResult> {
  return postRaw<SplitResult>(
    `/api/clients/${clientId}/categorizations/${categorizationId}/split`,
    { lines },
  );
}
